/**
 * Web Component wrapper for ToriWebAgent
 * 
 * This file creates a Custom Element (Web Component) that wraps the shared
 * ToriWidgetCore component, making it usable in any framework or plain HTML.
 * 
 * Configuration is baked in at build time via environment variables:
 * - VITE_AZURE_CLIENT_ID: Azure AD client ID for authentication
 * - VITE_AZURE_AUTHORITY: Azure AD authority URL (optional)
 * - VITE_IFRAME_URL: URL of the TORI chat iframe
 * 
 * Host Context (required):
 * At least one of the following identifiers must be provided:
 * - app-id: Application identifier
 * - platform-id: Platform identifier
 * - portfolio-id: Portfolio identifier
 *
 * Optional attributes:
 * - title: Title displayed in the widget header (defaults to "TORI")
 * 
 * Usage in HTML:
 * ```html
 * <script src="path/to/tori-web-agent.umd.js"></script>
 * <tori-web-agent app-id="my-application" title="My Assistant"></tori-web-agent>
 * ```
 * 
 * Or programmatically:
 * ```javascript
 * window.ToriWebAgent.init({ appId: 'my-application', title: 'My Assistant' });
 * ```
 */

import React, { useEffect, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { MsalProvider } from "@azure/msal-react";
import { PublicClientApplication, type Configuration } from "@azure/msal-browser";

// Import shared core components and utilities
import { ToriWidgetCore, isValidHostContext, HOST_CONTEXT_ERROR_MESSAGE } from './core';
import type { HostContext } from './core';

// Configuration from environment variables (baked in at build time)
const CLIENT_ID = import.meta.env.VITE_AZURE_CLIENT_ID;
const IFRAME_URL = import.meta.env.VITE_IFRAME_URL || 'http://localhost:3000/';
const AUTHORITY = import.meta.env.VITE_AZURE_AUTHORITY || 'https://login.microsoftonline.com/05d75c05-fa1a-42e7-9cf1-eb416c396f2d';

const msalConfigured = Boolean(CLIENT_ID);

function createMsalConfig(clientId: string, authority: string): Configuration {
  return {
    auth: {
      clientId,
      authority,
      redirectUri: window.location?.origin || '/',
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
  };
}

/**
 * Main widget component with MSAL provider for web component
 * 
 * This component initializes its own MSAL instance (since web components
 * are standalone and don't share context with the host app), then renders
 * the shared ToriWidgetCore.
 *
 * @param props.hostContext - Host context containing at least one identifier
 * @param props.title - Title displayed in the widget header. Defaults to "TORI".
 * @param props.slotPortal - Optional React node for the custom trigger (light-DOM slot)
 */
function ToriWidget({ hostContext, slotPortal, title, prompts }: { hostContext: HostContext; slotPortal?: React.ReactNode; title?: string; prompts?: string[] }) {
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);

  useEffect(() => {
    if (!CLIENT_ID) return;
    const config = createMsalConfig(CLIENT_ID, AUTHORITY);
    const instance = new PublicClientApplication(config);
    instance.initialize().then(() => setMsalInstance(instance)).catch(console.error);
  }, []);

  // Validate host context
  if (!isValidHostContext(hostContext)) {
    console.error(HOST_CONTEXT_ERROR_MESSAGE);
    return (
      <div style={{ color: '#a00', padding: '16px', textAlign: 'center' }}>
        {HOST_CONTEXT_ERROR_MESSAGE}
      </div>
    );
  }

  if (!msalConfigured) {
    return (
      <button className="tori-floating-button" aria-label="Open chat window" onClick={() => console.error('TORI: VITE_AZURE_CLIENT_ID environment variable is required during build')}>
        <img src="https://tori.maersk.io/tori-avatar.svg" style={{height: 58, width: 58}} alt="TORI" />
      </button>
    );
  }

  if (!msalInstance) {
    return null; // Loading
  }

  return (
    <MsalProvider instance={msalInstance}>
      <ToriWidgetCore 
        hostContext={hostContext} 
        iframeSrc={IFRAME_URL}
        msalConfigured={msalConfigured}
        title={title}
        prompts={prompts}
      >
        {slotPortal}
      </ToriWidgetCore>
    </MsalProvider>
  );
}

/**
 * Custom Element (Web Component) wrapper
 * 
 * Attributes:
 * - app-id: Application identifier (at least one of the three identifiers required)
 * - platform-id: Platform identifier
 * - portfolio-id: Portfolio identifier
 * - title: Title displayed in the widget header (optional, defaults to "TORI")
 * 
 * @example
 * ```html
 * <tori-web-agent app-id="my-application"></tori-web-agent>
 * <tori-web-agent platform-id="my-platform" title="My Assistant"></tori-web-agent>
 * <tori-web-agent portfolio-id="my-portfolio"></tori-web-agent>
 * ```
 */
/**
 * SlotPortal – Renders the content projected via the light-DOM <slot> inside
 * the React tree so it can be used as the custom widget trigger.
 */
function SlotPortal({ slotRef }: { slotRef: React.RefObject<HTMLSlotElement | null> }) {
  const [nodes, setNodes] = useState<Node[]>([]);

  useEffect(() => {
    const slot = slotRef.current;
    if (!slot) return;

    const update = () => {
      const assigned = slot.assignedNodes({ flatten: true });
      setNodes([...assigned]);
    };

    update();
    slot.addEventListener('slotchange', update);
    return () => slot.removeEventListener('slotchange', update);
  }, [slotRef]);

  if (nodes.length === 0) return null;

  // Render a container that adopts the slotted light-DOM nodes
  return React.createElement('span', {
    style: { display: 'contents' },
    ref: (el: HTMLSpanElement | null) => {
      if (el) {
        // Move the assigned (light-DOM) nodes into this span so they
        // live inside the React-managed tree while remaining in the
        // light DOM for styling purposes.
        nodes.forEach((n) => el.appendChild(n));
      }
    },
  });
}

class ToriWebAgentElement extends HTMLElement {
  private root: Root | null = null;
  private mountPoint: HTMLDivElement | null = null;
  private slotRef: React.RefObject<HTMLSlotElement | null> = { current: null };
  private hasCustomTrigger = false;

  static get observedAttributes() {
    return ['app-id', 'platform-id', 'portfolio-id', 'title', 'prompts'];
  }

  private getHostContext(): HostContext {
    return {
      appId: this.getAttribute('app-id') || undefined,
      platformId: this.getAttribute('platform-id') || undefined,
      portfolioId: this.getAttribute('portfolio-id') || undefined,
    };
  }

  connectedCallback() {
    // Detect if the consumer projected any light-DOM children (custom trigger)
    this.hasCustomTrigger = this.childNodes.length > 0;

    // Attach shadow DOM so we can use <slot> for content projection
    const shadow = this.attachShadow({ mode: 'open' });

    // The <slot> element projects light-DOM children into the shadow tree
    const slot = document.createElement('slot');
    slot.style.display = 'none'; // hidden; React will adopt the nodes
    shadow.appendChild(slot);
    this.slotRef = { current: slot };

    this.mountPoint = document.createElement('div');
    this.mountPoint.style.display = 'contents';
    shadow.appendChild(this.mountPoint);

    this.root = createRoot(this.mountPoint);
    this.render();
  }

  attributeChangedCallback() {
    if (this.root) {
      this.render();
    }
  }

  private render() {
    const hostContext = this.getHostContext();
    const title = this.getAttribute('title') || undefined;
    const promptsAttr = this.getAttribute('prompts');
    let prompts: string[] | undefined;
    if (promptsAttr) {
      try {
        const parsed = JSON.parse(promptsAttr);
        if (Array.isArray(parsed)) prompts = parsed;
      } catch {
        // ignore invalid JSON
      }
    }
    const slotPortal = this.hasCustomTrigger
      ? React.createElement(SlotPortal, { slotRef: this.slotRef })
      : undefined;
    this.root?.render(React.createElement(ToriWidget, { hostContext, slotPortal, title, prompts }));
  }

  disconnectedCallback() {
    if (this.root) { this.root.unmount(); this.root = null; }
    if (this.mountPoint) { this.mountPoint.remove(); this.mountPoint = null; }
  }
}

// Register the custom element
if (typeof window !== 'undefined' && !customElements.get('tori-web-agent')) {
  customElements.define('tori-web-agent', ToriWebAgentElement);
}

// Global API for programmatic usage
declare global {
  interface Window {
    ToriWebAgent: {
      init: (options?: { appId?: string; platformId?: string; portfolioId?: string; title?: string }) => void;
      version: string;
    };
  }
}

if (typeof window !== 'undefined') {
  window.ToriWebAgent = {
    version: '1.0.0',
    /**
     * Programmatically add the TORI widget to the page
     * @param options - Widget options
     * @param options.appId - Application identifier (at least one of appId, platformId, portfolioId required)
     * @param options.platformId - Platform identifier
     * @param options.portfolioId - Portfolio identifier
     * @param options.title - Title displayed in the widget header. Defaults to "TORI".
     * 
     * @example
     * window.ToriWebAgent.init({ appId: 'my-application', title: 'My Assistant' });
     */
    init: (options?: { appId?: string; platformId?: string; portfolioId?: string; title?: string; prompts?: string[] }) => {
      if (document.querySelector('tori-web-agent')) {
        console.warn('TORI widget is already initialized');
        return;
      }

      if (!options || (!options.appId && !options.platformId && !options.portfolioId)) {
        console.error(HOST_CONTEXT_ERROR_MESSAGE);
        return;
      }

      const element = document.createElement('tori-web-agent');
      if (options.appId) element.setAttribute('app-id', options.appId);
      if (options.platformId) element.setAttribute('platform-id', options.platformId);
      if (options.portfolioId) element.setAttribute('portfolio-id', options.portfolioId);
      if (options.title) element.setAttribute('title', options.title);
      if (options.prompts?.length) element.setAttribute('prompts', JSON.stringify(options.prompts));
      document.body.appendChild(element);
    },
  };
}

export { ToriWebAgentElement };
